import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, Download, Gift, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

function ExitIntentPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    // Check if popup was already shown this session
    const shown = sessionStorage.getItem('exitPopupShown');
    if (shown) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves from top of viewport
      if (e.clientY <= 0 && !hasShown) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    // Also show after 45 seconds of inactivity
    const inactivityTimer = setTimeout(() => {
      if (!hasShown) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    }, 45000);

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(inactivityTimer);
    };
  }, [hasShown]);

  const handleClose = () => {
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#006847] to-[#A4D65E] p-6 text-white relative">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <Gift className="h-8 w-8" />
              <h2 className="text-2xl font-bold">Wait! Free Gift Inside</h2>
            </div>
            <p className="text-white/90">Don't leave empty-handed!</p>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            <p className="text-gray-700">
              Get our <strong>FREE Louisiana Changemaker Starter Pack</strong> — six guides covering everything a Louisiana founder, nonprofit, or volunteer needs to start strong.
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-[#006847] mb-2">🔰 What's Included:</h3>
              <ul className="space-y-1 text-sm text-gray-700">
                <li>🔰 Business structure comparison (LLC, S-Corp, Nonprofit, more)</li>
                <li>🔰 Nonprofit 501(c)(3) formation checklist</li>
                <li>🔰 Permits & tax registration (for businesses + nonprofits)</li>
                <li>🔰 10 grants & funding opportunities (LA-specific)</li>
                <li>🔰 Volunteer + PROOF credential quick-start</li>
                <li>🔰 First 90 days action plan (any role)</li>
              </ul>
            </div>

            <div className="space-y-3">
              <Link to="/register" onClick={handleClose}>
                <Button className="w-full bg-gradient-to-r from-[#006847] to-[#A4D65E] hover:opacity-90 text-white">
                  <Download className="h-4 w-4 mr-2" />
                  Get Free Starter Pack
                </Button>
              </Link>
              <button
                onClick={handleClose}
                className="w-full text-sm text-gray-500 hover:text-gray-700 transition"
              >
                No thanks, I'll figure it out myself
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 text-center">
            <p className="text-xs text-gray-500">
              Join 150+ Louisiana entrepreneurs. 100% free, no spam.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default ExitIntentPopup;
